import { FocusRing } from '@react-aria/focus'
import { useTextField } from '@react-aria/textfield'
import { isEmpty } from 'lodash'
import React, { useRef } from 'react'
import { get, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import tw, { styled } from 'twin.macro'
import type { TTextInputProps } from './interface'
import { getInputStyle, StyledInput } from './styles'

const StyledInputText = styled.input(({ isError = false }: TTextInputProps) => [StyledInput, getInputStyle(isError)])

const TextInputField = (props: TTextInputProps) => {
  const {
    id,
    label,
    name,
    placeholder,
    required,
    disabled,
    errorMessage,
    isError,
    ariaLabel,
    value,
    onBlur,
    onChange,
    fieldRef,
    type = 'text',
  } = props
  const ref = useRef<HTMLInputElement | null>(null)
  const { errorMessageProps, inputProps } = useTextField({ 'aria-label': ariaLabel ?? 'Form input', ...props }, ref)

  return (
    <div css={tw`flex flex-col`}>
      <FocusRing focusRingClass="focus-ring">
        <StyledInputText
          {...inputProps}
          label={label}
          ref={(e) => {
            fieldRef?.(e)
            ref.current = e
          }}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          id={id}
          name={name}
          errorMessage={errorMessage}
          isError={isError || !!errorMessage}
          aria-label={ariaLabel}
          aria-labelledby={id}
          value={value}
          onBlur={onBlur}
          onChange={onChange}
          type={type}
        />
      </FocusRing>
      {errorMessage && (
        <div {...errorMessageProps} css={tw`text-sm text-red-500 px-6`}>
          {errorMessage}
        </div>
      )}
    </div>
  )
}

export const ReactHookFormInput = (props: TTextInputProps) => {
  const { name, required, minLength = 0, maxLength = 99999999, validation } = props
  const { register, formState } = useFormContext()
  const error: Error = get(formState.errors, name)
  const errMsg = error?.message ?? undefined
  const { t } = useTranslation('common')
  const { ref: refCallback, ...rest } = register(name, {
    required: required ? t('FORM.REQUIRED') ?? 'required' : false,
    minLength: {
      value: minLength,
      message: t('FORM.MIN_LENGTH', { length: minLength }),
    },
    maxLength: {
      value: maxLength,
      message: t('FORM.MAX_LENGTH', { length: minLength }),
    },
    ...validation,
  })

  return <TextInputField fieldRef={refCallback} {...rest} {...props} isError={!isEmpty(errMsg)} errorMessage={errMsg} />
}

export default TextInputField
